import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, Search, ChevronUp, ChevronDown, X } from 'lucide-react'
import { useAdminLeagues, useAdminLeagueConfigs, useAdminMemberCounts, useAdminLeagueOwners } from '@/hooks/useAdminData'

interface LeagueRow {
  id: string
  name: string
  season: string
  created_at: string
  settings: Record<string, unknown> | null
  competition: string
  memberCount: number
  ownerName: string
}

type SortKey = 'name' | 'competition' | 'season' | 'memberCount' | 'ownerName' | 'created_at'

export default function AdminLeagues() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('created_at')
  const [sortAsc, setSortAsc] = useState(false)
  const [filterCompetition, setFilterCompetition] = useState('')
  const [filterSeason, setFilterSeason] = useState('')

  const { data: leagues = [], isLoading } = useAdminLeagues()
  const { data: configs = [] } = useAdminLeagueConfigs()
  const { data: memberCounts = [] } = useAdminMemberCounts()
  const { data: owners = [] } = useAdminLeagueOwners()

  const rows: LeagueRow[] = useMemo(() => {
    return leagues.map((l) => ({
      id: l.id,
      name: l.name,
      season: l.season,
      created_at: l.created_at,
      settings: l.settings as Record<string, unknown> | null,
      competition: configs.find(c => c.league_id === l.id)?.competition_name ?? 'Unknown',
      memberCount: memberCounts.find(m => m.league_id === l.id)?.count ?? 0,
      ownerName: owners.find(o => o.league_id === l.id)?.display_name ?? '—',
    }))
  }, [leagues, configs, memberCounts, owners])

  const uniqueCompetitions = useMemo(() => [...new Set(rows.map(r => r.competition))].sort(), [rows])
  const uniqueSeasons = useMemo(() => [...new Set(rows.map(r => r.season))].sort().reverse(), [rows])

  const filtered = useMemo(() => {
    let result = [...rows]
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(r =>
        r.name.toLowerCase().includes(q) ||
        r.ownerName.toLowerCase().includes(q) ||
        r.competition.toLowerCase().includes(q)
      )
    }
    if (filterCompetition) result = result.filter(r => r.competition === filterCompetition)
    if (filterSeason) result = result.filter(r => r.season === filterSeason)

    result.sort((a, b) => {
      const aVal = a[sortKey]
      const bVal = b[sortKey]
      if (typeof aVal === 'number' && typeof bVal === 'number') return sortAsc ? aVal - bVal : bVal - aVal
      const aStr = String(aVal ?? '')
      const bStr = String(bVal ?? '')
      return sortAsc ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
    })
    return result
  }, [rows, search, filterCompetition, filterSeason, sortKey, sortAsc])

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortAsc(!sortAsc)
    } else {
      setSortKey(key)
      setSortAsc(true)
    }
  }

  function SortIcon({ column }: { column: SortKey }) {
    if (sortKey !== column) return null
    return sortAsc
      ? <ChevronUp className="h-3 w-3 inline ml-0.5" />
      : <ChevronDown className="h-3 w-3 inline ml-0.5" />
  }

  const hasFilters = filterCompetition || filterSeason

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">Leagues</h1>

      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <input
            type="text"
            placeholder="Search leagues, owners, competitions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                       bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                       focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
          />
        </div>
        <select
          value={filterCompetition}
          onChange={(e) => setFilterCompetition(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                     bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100"
        >
          <option value="">All Competitions</option>
          {uniqueCompetitions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          value={filterSeason}
          onChange={(e) => setFilterSeason(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                     bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100"
        >
          <option value="">All Seasons</option>
          {uniqueSeasons.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {hasFilters && (
          <button
            onClick={() => { setFilterCompetition(''); setFilterSeason(''); }}
            className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
          >
            <X className="h-3 w-3" /> Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100 dark:border-zinc-800 text-xs text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
              <th className="px-4 py-3 text-left font-medium cursor-pointer select-none" onClick={() => handleSort('name')}>
                Name <SortIcon column="name" />
              </th>
              <th className="px-4 py-3 text-left font-medium cursor-pointer select-none" onClick={() => handleSort('competition')}>
                Competition <SortIcon column="competition" />
              </th>
              <th className="px-4 py-3 text-left font-medium cursor-pointer select-none" onClick={() => handleSort('season')}>
                Season <SortIcon column="season" />
              </th>
              <th className="px-4 py-3 text-right font-medium cursor-pointer select-none" onClick={() => handleSort('memberCount')}>
                Members <SortIcon column="memberCount" />
              </th>
              <th className="px-4 py-3 text-left font-medium cursor-pointer select-none" onClick={() => handleSort('ownerName')}>
                Creator <SortIcon column="ownerName" />
              </th>
              <th className="px-4 py-3 text-left font-medium cursor-pointer select-none" onClick={() => handleSort('created_at')}>
                Created <SortIcon column="created_at" />
              </th>
              <th className="px-4 py-3 text-left font-medium">Payment</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {filtered.map((row) => (
              <tr
                key={row.id}
                onClick={() => navigate(`/admin/leagues/${row.id}`)}
                className="cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
              >
                <td className="px-4 py-3 font-medium text-zinc-900 dark:text-zinc-100">{row.name}</td>
                <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400">{row.competition}</td>
                <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400">{row.season}</td>
                <td className="px-4 py-3 text-right text-zinc-600 dark:text-zinc-400">{row.memberCount}</td>
                <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400">{row.ownerName}</td>
                <td className="px-4 py-3 text-zinc-500 dark:text-zinc-500">
                  {new Date(row.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400">
                    {(row.settings?.payment_status as string) || 'waived'}
                  </span>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-zinc-400">
                  {search || hasFilters ? 'No leagues match your search' : 'No leagues found'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-zinc-400">{filtered.length} of {rows.length} leagues</p>
    </div>
  )
}
